import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, RotateCcw, FileSignature } from "lucide-react";
import { PRICE_TYPES, CONTRACT_STATUS } from "@/lib/scadenze";
import { eur } from "@/lib/crm";

const EMPTY = {
  supplier: "", offer_name: "", subscription_date: "", activation_date: "",
  economic_start_date: "", economic_end_date: "", price_type: "", energy_price: "",
  price_unit: "", fixed_fee: "", status: "Bozza", notes: "",
};

// mode: "create" | "edit" | "renew"
export default function ContractDialog({ open, onOpenChange, mode, supply, contract, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isRenew = mode === "renew";
  const isEdit = mode === "edit";
  const priceUnit = supply?.type === "Gas" ? "€/Smc" : "€/kWh";

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit && contract) {
      setF({
        supplier: contract.supplier || "", offer_name: contract.offer_name || "",
        subscription_date: contract.subscription_date || "", activation_date: contract.activation_date || "",
        economic_start_date: contract.economic_start_date || "", economic_end_date: contract.economic_end_date || "",
        price_type: contract.price_type || "", energy_price: contract.energy_price ?? "",
        price_unit: contract.price_unit || "", fixed_fee: contract.fixed_fee ?? "",
        status: contract.status || "Bozza", notes: contract.notes || "",
      });
    } else if (isRenew && contract) {
      // Precompila i dati anagrafici dal precedente, ma azzera date/prezzi per il nuovo periodo
      setF({ ...EMPTY, supplier: contract.supplier || "", price_unit: contract.price_unit || priceUnit, status: "Inserito" });
    } else {
      setF({ ...EMPTY, price_unit: priceUnit });
    }
  }, [open, mode, contract, isEdit, isRenew, priceUnit]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {
        ...f,
        subscription_date: f.subscription_date || null, activation_date: f.activation_date || null,
        economic_start_date: f.economic_start_date || null, economic_end_date: f.economic_end_date || null,
        price_type: f.price_type || null, price_unit: f.price_unit || null,
        energy_price: f.energy_price === "" ? null : Number(f.energy_price),
        fixed_fee: f.fixed_fee === "" ? null : Number(f.fixed_fee),
      };
      let data;
      if (isRenew) ({ data } = await api.post(`/contracts/${contract.id}/renew`, payload));
      else if (isEdit) ({ data } = await api.put(`/contracts/${contract.id}`, payload));
      else ({ data } = await api.post(`/supplies/${supply.id}/contracts`, payload));
      toast.success(isRenew ? "Contratto rinnovato (storico conservato)" : isEdit ? "Contratto aggiornato" : "Contratto inserito");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const title = isRenew ? "Rinnova contratto" : isEdit ? "Modifica contratto" : "Nuovo contratto";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {isRenew ? <RotateCcw className="h-5 w-5 text-brand" /> : <FileSignature className="h-5 w-5 text-brand" />} {title}
          </DialogTitle>
          <DialogDescription>
            {isRenew
              ? "Il contratto precedente NON viene modificato: resta nello storico come Rinnovato. Prezzi e condizioni non sono obbligatori."
              : "Registra le condizioni economiche. Prezzi e condizioni non sono obbligatori se non ancora noti."}
          </DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="contract-error">{error}</div>}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fornitore</Label><Input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="contract-supplier" /></div>
            <div className="space-y-1.5"><Label>Nome offerta</Label><Input value={f.offer_name} onChange={(e) => set("offer_name", e.target.value)} data-testid="contract-offer" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Data sottoscrizione</Label><Input type="date" value={f.subscription_date} onChange={(e) => set("subscription_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Data attivazione</Label><Input type="date" value={f.activation_date} onChange={(e) => set("activation_date", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Inizio condizioni economiche</Label><Input type="date" value={f.economic_start_date} onChange={(e) => set("economic_start_date", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label className="font-semibold text-brand">Scadenza condizioni economiche</Label>
              <Input type="date" value={f.economic_end_date} onChange={(e) => set("economic_end_date", e.target.value)} data-testid="contract-end-date" />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Tipologia prezzo</Label>
              <Select value={f.price_type || "none"} onValueChange={(v) => set("price_type", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="contract-price-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{PRICE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Prezzo energia</Label><Input type="number" step="0.00001" value={f.energy_price} onChange={(e) => set("energy_price", e.target.value)} data-testid="contract-price" /></div>
            <div className="space-y-1.5"><Label>Unità</Label><Input value={f.price_unit} onChange={(e) => set("price_unit", e.target.value)} placeholder={priceUnit} /></div>
            <div className="space-y-1.5"><Label>Quota fissa (€)</Label><Input type="number" step="0.01" value={f.fixed_fee} onChange={(e) => set("fixed_fee", e.target.value)} /></div>
          </div>

          {isRenew && (() => {
            const annual = supply?.annual_consumption;
            const est = (p, fee) => (p == null || p === "" || annual == null) ? null : (Number(p) * Number(annual) + (fee ? Number(fee) * 12 : 0));
            const oldE = est(contract?.energy_price, contract?.fixed_fee);
            const newE = est(f.energy_price, f.fixed_fee);
            const ready = oldE != null && newE != null;
            const delta = ready ? newE - oldE : null;
            const pct = ready && oldE ? (delta / oldE) * 100 : null;
            const saving = ready && delta < 0;
            return (
              <div className={`rounded-lg border p-3 ${ready ? (saving ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5") : "border-border bg-slate-50"}`} data-testid="renewal-savings">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Confronto risparmio stimato (annuo)</Label>
                {!ready ? (
                  <p className="mt-1 text-sm text-slate-500">Dati non sufficienti: inserisci prezzo energia e (se disponibile) quota fissa; serve anche il consumo annuo della fornitura.</p>
                ) : (
                  <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                    <div><div className="text-xs text-slate-500">Attuale</div><div className="font-semibold text-slate-700">{eur(oldE)}/anno</div></div>
                    <div><div className="text-xs text-slate-500">Nuova offerta</div><div className="font-semibold text-slate-700">{eur(newE)}/anno</div></div>
                    <div><div className="text-xs text-slate-500">Differenza</div>
                      <div className={`font-bold ${saving ? "text-success" : "text-warning"}`} data-testid="savings-delta">
                        {saving ? "−" : "+"}{eur(Math.abs(delta))} ({pct != null ? `${Math.abs(pct).toFixed(1)}%` : "—"})
                      </div>
                    </div>
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-400">Stima indicativa = prezzo energia × consumo annuo + quota fissa × 12.</p>
              </div>
            );
          })()}

          <div className="space-y-1.5">
            <Label>Stato contratto</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="contract-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{CONTRACT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="contract-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRenew ? "Rinnova" : "Salva")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
